import { useMemo } from 'react'
import {
  OwnershipSchedulingLink2,
  OwnershipSchedulingLinkAssignments1,
  ProductFeatureV3,
  RoundRobinExpectedHostV2,
} from '@chilipiper/api-client/src/user-availability'
import {
  ArrayAlwaysInvitedUser,
  OwnershipRoutingResponse,
  OwnershipSettingsV4,
  PublicOwnershipSchedulingLinkV2,
  PublicOwnershipSchedulingLinkV2Assignment,
  PublicOwnershipSchedulingLinkV2DistributionHost,
  PublicOwnershipSchedulingLinkV2OwnershipHost,
} from '@chilipiper/api-client/src/team-scheduling'
import { UserSlugResolution } from '@chilipiper/api-client/src/firerouter'
import invariant from 'tiny-invariant'
import {
  BookerInvitationSettings,
  getBookerInvitationSettings,
  getRoundRobinAndOwnershipExpectedHost,
} from '../../load-availability-options'
import {
  getCrmExplicitsFromMeetingAttemptProductFeature,
  getDistributionIdsFromOwnershipAssignments,
  getDistributionIdsFromAssigneeEvaluation,
  transformOwnershipAssignmentsToRoutingResult,
  transformAssigneeEvaluationToRoutingResult,
  applySelectedAssigneesToRoutingResult,
  getFirstAssignmentFromRoutingResult,
} from '../helpers'
import { LinkResourceEnhanced, OwnershipLinkRoutingResult } from '../types'
import { createCrmExplicits } from '../../smart-parameters/helpers'
import { useDistributionsUserList } from '../api-queries'
import { useRouteGuestToOwnershipSchedulingLink } from '../api-queries/ownership'
import { BookerInvitation } from '../../../types'
import { useParsedGuest } from './use-parsed-guest'

type OwnershipMatch = {
  expectedHost: RoundRobinExpectedHostV2
  ownershipLinkRoutingResult: OwnershipLinkRoutingResult
  primaryGuestEmail?: string
  alwaysInvitedUsers?: ArrayAlwaysInvitedUser
  bookerInvitationSettings?: BookerInvitationSettings
}

type OwnershipSchedulingLink = Pick<
  PublicOwnershipSchedulingLinkV2,
  'distribution' | 'ownership' | 'linkId'
>

type OwnershipRoutingCommon = {
  tenantId?: string
  smartParameterId?: string | null
  productFeature?: OwnershipSchedulingLink2
  searchParams: URLSearchParams
  selectedAssigneeIds?: string[]
  bookerFromSmartParameter?: UserSlugResolution
  meetingAttemptBookerId?: string
}

type OwnershipRoutingParams = {
  ownershipSchedulingLink?: OwnershipSchedulingLink
} & OwnershipRoutingCommon

type OwnershipRoutingWithLinkResource = {
  linkResource?: LinkResourceEnhanced
  ownershipRouting?: OwnershipRoutingResponse
} & Omit<OwnershipRoutingCommon, 'searchParams'>

export const useOwnershipRouting = ({
  bookerFromSmartParameter,
  meetingAttemptBookerId,
  ownershipSchedulingLink,
  productFeature,
  searchParams,
  selectedAssigneeIds,
  smartParameterId,
  tenantId,
}: OwnershipRoutingParams): OwnershipMatch | undefined => {
  const { ownershipRouting, primaryGuestEmail, isRouting } = useRouteGuest({
    tenantId,
    smartParameterId,
    productFeature,
    searchParams,
    linkId: ownershipSchedulingLink?.linkId,
    ownershipSettings: ownershipSchedulingLink?.ownership.ownershipSettings,
  })

  const { alwaysInvitedUsers, bookerInvitation, host } = getMatchRouteData(
    ownershipSchedulingLink,
    ownershipRouting
  )

  const routingEvaluation = useEvaluateOwnershipRouting({
    tenantId,
    selectedAssigneeIds,
    bookerInvitation,
    ownershipRouting,
    productFeature,
    bookerFromSmartParameter,
    bookerId: meetingAttemptBookerId,
    distributionAssignments: ownershipSchedulingLink?.distribution.assignments,
    ownershipSettings: ownershipSchedulingLink?.ownership.ownershipSettings,
    host,
    isLoading: isRouting,
  })

  if (!routingEvaluation) {
    return undefined
  }

  return {
    primaryGuestEmail,
    expectedHost: routingEvaluation.expectedHost,
    alwaysInvitedUsers,
    bookerInvitationSettings: routingEvaluation.bookerInvitationSettings,
    ownershipLinkRoutingResult: routingEvaluation.ownershipLinkRoutingResult,
  } satisfies OwnershipMatch
}

export const useOwnershipRoutingWithLinkResource = ({
  bookerFromSmartParameter,
  linkResource,
  meetingAttemptBookerId,
  productFeature,
  selectedAssigneeIds,
  smartParameterId,
  tenantId,
}: OwnershipRoutingWithLinkResource) => {
  const ownershipResource =
    linkResource?.type === 'OwnershipSchedulingLink' ? linkResource : undefined

  const parsedGuest = useParsedGuest({
    smartParameterId,
    linkProductFeature: productFeature,
  })

  const routingEvaluation = useEvaluateOwnershipRouting({
    tenantId,
    selectedAssigneeIds,
    bookerInvitation: ownershipResource?.bookerInvitation,
    distributionAssignments: ownershipResource?.assignments,
    ownershipSettings: ownershipResource?.ownershipSettings,
    ownershipRouting: ownershipResource?.routing,
    productFeature,
    bookerFromSmartParameter,
    bookerId: meetingAttemptBookerId,
    host: ownershipResource?.host,
  })

  if (!routingEvaluation || !ownershipResource) {
    return undefined
  }

  return {
    expectedHost: routingEvaluation.expectedHost,
    primaryGuestEmail: parsedGuest?.guestEmail,
    alwaysInvitedUsers: ownershipResource.alwaysInvitedUsers,
    bookerInvitationSettings: routingEvaluation.bookerInvitationSettings,
    ownershipLinkRoutingResult: routingEvaluation.ownershipLinkRoutingResult,
  } satisfies OwnershipMatch
}

type OwnershipRoutingEvaluation = {
  bookerInvitationSettings: BookerInvitationSettings
  expectedHost: RoundRobinExpectedHostV2
  ownershipLinkRoutingResult: OwnershipLinkRoutingResult
}

type EvaluateOwnershipRouting = {
  tenantId?: string
  selectedAssigneeIds?: string[]
  ownershipRouting?: OwnershipRoutingResponse
  productFeature?: OwnershipSchedulingLink2
  bookerFromSmartParameter?: UserSlugResolution
  distributionAssignments?: PublicOwnershipSchedulingLinkV2Assignment[]
  ownershipSettings?: OwnershipSettingsV4
  bookerId?: string
  bookerInvitation?: BookerInvitation
  isLoading?: boolean
  host?:
    | PublicOwnershipSchedulingLinkV2OwnershipHost
    | PublicOwnershipSchedulingLinkV2DistributionHost
}

const useEvaluateOwnershipRouting = ({
  ownershipRouting,
  productFeature,
  tenantId,
  selectedAssigneeIds,
  bookerFromSmartParameter,
  bookerId,
  distributionAssignments,
  ownershipSettings,
  bookerInvitation,
  host,
  isLoading,
}: EvaluateOwnershipRouting): OwnershipRoutingEvaluation | undefined => {
  const assignments = productFeature?.assignments

  const distributionIds = useMemo(
    () =>
      getAssignmentDistributionIds({
        distributionRouteAssignments: distributionAssignments,
        routing: ownershipRouting,
        meetingAttemptAssignments: assignments,
        isLoading,
      }),
    [assignments, ownershipRouting, distributionAssignments, isLoading]
  )

  const distributions = useDistributionsUserList({
    tenantId: tenantId ?? '',
    distributionIds,
    productFeature: 'OwnershipSchedulingLink',
  })

  const isLoadingDistributions = useMemo(() => {
    return distributionIds.length > 0 && distributions.length === 0
  }, [distributionIds, distributions])

  const ownershipLinkRoutingResult = assignments
    ? transformOwnershipAssignmentsToRoutingResult(
        assignments,
        distributions,
        distributionAssignments,
        ownershipSettings,
        ownershipRouting?.assigneesEvaluationV3
      )
    : transformAssigneeEvaluationToRoutingResult(
        ownershipRouting?.assigneesEvaluationV3,
        distributions,
        distributionAssignments,
        ownershipSettings
      )

  // Apply selectedAssigneeId to the routing result if provided
  const updatedRoutingResult = applySelectedAssigneesToRoutingResult(
    ownershipLinkRoutingResult,
    selectedAssigneeIds
  )

  if (!updatedRoutingResult || isLoadingDistributions) {
    return undefined
  }

  const bookerInvitationSettings = getBookerInvitationSettings({
    bookerFromSmartParameter,
    bookerInvitation,
    meetingAttemptBookerId: bookerId,
  })

  const firstAssignment = getFirstAssignmentFromRoutingResult(updatedRoutingResult)
  const expectedHost = getRoundRobinAndOwnershipExpectedHost({
    bookerInvitationSettings,
    firstAssignment: firstAssignment,
    host,
  })

  return {
    expectedHost,
    bookerInvitationSettings,
    ownershipLinkRoutingResult: updatedRoutingResult,
  }
}

type RouteGuest = {
  primaryGuestEmail?: string
  ownershipRouting?: OwnershipRoutingResponse
  isRouting: boolean
}

const useRouteGuest = ({
  productFeature,
  smartParameterId,
  tenantId,
  searchParams,
  linkId,
  ownershipSettings,
}: {
  tenantId?: string
  smartParameterId?: string | null
  productFeature?: ProductFeatureV3
  searchParams: URLSearchParams
  linkId?: string
  ownershipSettings?: OwnershipSettingsV4
}): RouteGuest => {
  const parsedGuest = useParsedGuest({
    smartParameterId,
    linkProductFeature:
      productFeature?.type === 'OwnershipSchedulingLink' ? productFeature : undefined,
  })

  const primaryGuestEmail = parsedGuest?.guestEmail
  const assignments =
    productFeature?.type === 'OwnershipSchedulingLink' ? productFeature.assignments : undefined

  const { data: ownershipRouting, isLoading } = useRouteGuestToOwnershipSchedulingLink({
    tenantId,
    ownershipSchedulingLinkId: linkId,
    data:
      parsedGuest &&
      (!assignments ||
        (assignments.type === 'WithLegacySimpleRule' &&
          ownershipSettings?.type === 'WithMixedRouting'))
        ? {
            ...parsedGuest,
            crmExplicits: createCrmExplicits(
              searchParams,
              getCrmExplicitsFromMeetingAttemptProductFeature(productFeature)
            ),
          }
        : undefined,
  })

  return {
    primaryGuestEmail,
    ownershipRouting,
    isRouting: isLoading,
  }
}

const getAssignmentDistributionIds = ({
  meetingAttemptAssignments,
  routing,
  distributionRouteAssignments,
  isLoading,
}: {
  meetingAttemptAssignments?: OwnershipSchedulingLinkAssignments1
  routing?: OwnershipRoutingResponse
  isLoading?: boolean
  distributionRouteAssignments?: PublicOwnershipSchedulingLinkV2Assignment[]
}) => {
  if (meetingAttemptAssignments) {
    return getDistributionIdsFromOwnershipAssignments(
      meetingAttemptAssignments,
      distributionRouteAssignments?.map(dist => dist.distributionId)
    )
  }

  if (isLoading) {
    return []
  }

  if (routing?.assigneesEvaluationV3) {
    return getDistributionIdsFromAssigneeEvaluation(routing.assigneesEvaluationV3)
  }

  return distributionRouteAssignments?.map(dist => dist.distributionId) ?? []
}

const getMatchRouteData = (
  ownershipSchedulingLink?: OwnershipSchedulingLink,
  routing?: OwnershipRoutingResponse
) => {
  invariant(ownershipSchedulingLink, 'ownershipSchedulingLink is required')

  if (routing?.assigneesEvaluationV3.type !== 'Distributed') {
    return {
      bookerInvitation: ownershipSchedulingLink.ownership.bookerInvitation,
      host: ownershipSchedulingLink.ownership.host,
      alwaysInvitedUsers: ownershipSchedulingLink.ownership.alwaysInvitedUsers,
    }
  }

  // No owner found, use distribution path
  return {
    bookerInvitation: ownershipSchedulingLink.distribution.bookerInvitation,
    host: ownershipSchedulingLink.distribution.host,
    alwaysInvitedUsers: ownershipSchedulingLink.distribution.alwaysInvitedUsers,
  }
}
